import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  CloudDownload as ImportIcon,
} from '@mui/icons-material';
import { Listing } from '@/types';
import { Link } from 'react-router-dom';

interface ListingCardProps {
  listing: Listing;
  onStatusChange?: (listingId: string, status: 'published' | 'paused') => void;
  onDelete?: (listingId: string) => void;
}

export const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onStatusChange,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (status: 'published' | 'paused') => {
    onStatusChange?.(listing.listingId, status);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      onDelete?.(listing.listingId);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'paused':
        return 'warning';
      case 'draft':
        return 'default';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published':
        return 'Published';
      case 'paused':
        return 'Paused';
      case 'draft':
        return 'Draft';
      case 'inactive':
        return 'Needs Review';
      default:
        return status;
    }
  };

  const mainPhoto = listing.photos[0] || '/placeholder-property.jpg';
  const currency = listing.pricing.currency === 'USD' ? '$' : listing.pricing.currency;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={mainPhoto}
          alt={listing.title}
          sx={{ objectFit: 'cover' }}
        />
        
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1, flexDirection: 'column' }}>
          <Chip
            label={getStatusLabel(listing.status)}
            color={getStatusColor(listing.status) as any}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />

        </Box>
        
        <IconButton
          onClick={handleMenuClick}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom noWrap>
          {listing.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {listing.address.city}, {listing.address.state}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {listing.maxGuests} guests • {listing.bedrooms} bedrooms • {listing.bathrooms} bathrooms
        </Typography>
        
        {listing.propertyType && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {listing.propertyType} • {listing.roomType || 'Entire place'}
          </Typography>
        )}
        

        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            {currency}{listing.pricing.basePrice}
            <Typography component="span" variant="body2" color="text.secondary">
              /night
            </Typography>
          </Typography>
          
          <Typography variant="caption" color="text.secondary">
            {listing.photos.length} photos
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        {listing.status === 'inactive' ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            component={Link}
            to={`/listings/${listing.listingId}`}
            fullWidth
          >
            Review & Activate
          </Button>
        ) : (
          <>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              component={Link}
              to={`/listings/${listing.listingId}`}
            >
              View
            </Button>
            
            <Button
              size="small"
              startIcon={<EditIcon />}
              component={Link}
              to={`/listings/${listing.listingId}`}
            >
              Edit
            </Button>
          </>
        )}
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {listing.status === 'draft' && (
          <MenuItem onClick={() => handleStatusChange('published')}>
            <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
            Publish
          </MenuItem>
        )}
        
        {listing.status === 'published' && (
          <MenuItem onClick={() => handleStatusChange('paused')}>
            <PauseIcon sx={{ mr: 1 }} fontSize="small" />
            Pause
          </MenuItem>
        )}
        
        {listing.status === 'paused' && (
          <MenuItem onClick={() => handleStatusChange('published')}>
            <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
            Resume
          </MenuItem>
        )}
        
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default ListingCard;